// originally written by @imoaazahmed

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBrand } from "@webuild-ai/react-ui/hooks";

const ThemeProps = {
  key: "theme",
  light: "light",
  dark: "dark",
} as const;

type Theme = typeof ThemeProps.light | typeof ThemeProps.dark;

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  isLight: boolean;
  isReady: boolean;
  setLightTheme: () => void;
  setDarkTheme: () => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { brand } = useBrand();

  /**
   * Mirror react-ui's semantic colour roles onto the legacy CSS variable names
   * still read by globals.css and some components. The react-ui `BrandProvider`
   * is the single writer of the canonical `--brand-*` tokens (`--brand-primary`,
   * `--brand-surface`, `--brand-accent`, `--brand-light-bg`, `--brand-dark-bg`,
   * `--brand-font-family`); these aliases are temporary and retire once the
   * globals.css cleanup lands. See docs/architecture/decisions/0017-migrate-brand-tenant-system-react-auth-to-react-ui.md.
   */
  const applyLegacyAliases = useCallback(
    (nextTheme: Theme) => {
      const isDark = nextTheme === ThemeProps.dark;
      const colours = isDark ? brand.colours.dark : brand.colours.light;
      const root = document.documentElement;

      root.style.setProperty("--current-color", colours.primary);
      root.style.setProperty("--brand-secondary", colours.surface);
      root.style.setProperty("--brand-tertiary", colours.accent);
      root.style.setProperty("--default-font-family", brand.fontFamily);
      // --brand-dark-bg-gradient is deliberately not written here. globals.css
      // declares it per brand, and an inline custom property beats any selector,
      // so writing one shared value from this hook silently overrode every
      // per-brand rule. That is what left the LSEG dark page on the WeBuild AI
      // gradient while :root[data-brand="lseg"].dark asked for a flat #010217,
      // showing as a seam against the embedded risk dashboard, whose own page
      // reads the LSEG pack's dark page colour directly.
    },
    [brand],
  );

  // Initialise theme from localStorage; only set the class here, brand-derived
  // aliases are applied in the effect below once the brand is resolved.
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(ThemeProps.key) as Theme | null;
    const initialTheme = storedTheme || ThemeProps.light;

    document.documentElement.classList.remove(ThemeProps.light, ThemeProps.dark);
    document.documentElement.classList.add(initialTheme);

    return initialTheme;
  });

  // Track whether the first alias pass has run, to avoid a flash of the wrong
  // colours before the brand is applied.
  const [isReady, setIsReady] = useState(false);

  const isDark = useMemo(() => theme === ThemeProps.dark, [theme]);
  const isLight = useMemo(() => theme === ThemeProps.light, [theme]);

  const _setTheme = useCallback(
    (newTheme: Theme) => {
      localStorage.setItem(ThemeProps.key, newTheme);
      document.documentElement.classList.remove(ThemeProps.light, ThemeProps.dark);
      document.documentElement.classList.add(newTheme);
      applyLegacyAliases(newTheme);
      setTheme(newTheme);
    },
    [applyLegacyAliases],
  );

  const setLightTheme = useCallback(() => _setTheme(ThemeProps.light), [_setTheme]);

  const setDarkTheme = useCallback(() => _setTheme(ThemeProps.dark), [_setTheme]);

  const toggleTheme = useCallback(
    () => (theme === ThemeProps.dark ? setLightTheme() : setDarkTheme()),
    [theme, setLightTheme, setDarkTheme],
  );

  // Re-apply legacy aliases whenever the brand or theme changes.
  useEffect(() => {
    applyLegacyAliases(theme);
    setIsReady(true);
  }, [brand, theme, applyLegacyAliases]);

  const value = useMemo(
    () => ({
      theme,
      isDark,
      isLight,
      isReady,
      setLightTheme,
      setDarkTheme,
      toggleTheme,
    }),
    [theme, isDark, isLight, isReady, setLightTheme, setDarkTheme, toggleTheme],
  );

  // Hold rendering until the first alias pass to prevent a colour flash.
  if (!isReady) {
    return null;
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
